import { useState } from "react";
import type { ReactNode } from "react";
import { cx } from "../utils/cx";
import EnvSwitcher from "./EnvSwitcher";
import type { DeployEnv } from "./EnvSwitcher";
import ProfileMenu from "./ProfileMenu";
import styles from "./AppLayout.module.css";

export interface NavItem {
  id: string;
  label: string;
  /** Optional inline-SVG icon (stroke="currentColor"). */
  icon?: ReactNode;
}

interface AppLayoutProps {
  env: DeployEnv;
  user: { name: string; email: string };
  nav: NavItem[];
  activeNavId: string;
  onNavigate: (id: string) => void;
  onSignOut?: () => void;
  children: ReactNode;
}

/**
 * The KCC application shell (style-guide.md: Application Shell). A persistent
 * dark rail on the left — dark in both themes — with the environment
 * indicator, primary nav, and the profile menu; a scrollable main region on
 * the right. The rail collapses to an icon strip.
 */
function AppLayout({
  env,
  user,
  nav,
  activeNavId,
  onNavigate,
  onSignOut,
  children,
}: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className={styles.shell}>
      <aside className={cx(styles.sidebar, collapsed && styles.collapsed)}>
        <div className={styles.brandRow}>
          {!collapsed && <EnvSwitcher env={env} />}
          <button
            type="button"
            className={styles.collapseBtn}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            aria-pressed={collapsed}
            onClick={() => setCollapsed((v) => !v)}
          >
            <ChevronIcon flipped={collapsed} />
          </button>
        </div>

        <nav className={styles.nav}>
          {nav.map((item) => {
            const active = item.id === activeNavId;
            return (
              <button
                key={item.id}
                type="button"
                className={cx(styles.navItem, active && styles.navActive)}
                aria-current={active ? "page" : undefined}
                title={collapsed ? item.label : undefined}
                onClick={() => onNavigate(item.id)}
              >
                {item.icon && <span className={styles.navIcon}>{item.icon}</span>}
                {!collapsed && <span className={styles.navLabel}>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className={styles.userArea}>
          <ProfileMenu name={user.name} email={user.email} onSignOut={onSignOut} />
          {!collapsed && (
            <div className={styles.userMeta}>
              <div className={styles.userName}>{user.name}</div>
              <div className={styles.userEmail}>{user.email}</div>
            </div>
          )}
        </div>
      </aside>

      <main className={styles.main}>{children}</main>
    </div>
  );
}

function ChevronIcon({ flipped }: { flipped: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: flipped ? "rotate(180deg)" : "none" }}
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

export default AppLayout;
